import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';

interface UseBatchSummariesParams {
  /** ``"completed"`` (default) — batches where every job is terminal.
   *  Used by the main-feed merge so completed series surface as
   *  cards in the feed without dup'ing the top-of-feed dashboard.
   *  ``"active"`` and ``"all"`` are exposed for symmetry but the
   *  Mini App today only consumes ``"completed"``. */
  state?: 'active' | 'completed' | 'all';
  /** Max number of summaries returned. Backend caps at 200; the
   *  Mini App defaults to 50 since the merged feed already has
   *  task cards in the same view. */
  limit?: number;
}

/** Phase 4.13.1 — poll ``GET /api/v1/batches/summaries`` for the
 *  feed-merge.
 *
 *  60-second ``refetchInterval`` (slower than ``useBatches`` 30 s
 *  because completed batches don't tick forward — the only thing
 *  that changes is a new completion being added at the top, which
 *  is fine to surface a minute late). ``staleTime`` 30 s so a
 *  back-nav from JobDetail doesn't trigger a re-fetch immediately.
 */
export function useBatchSummaries({
  state = 'completed',
  limit = 50,
}: UseBatchSummariesParams = {}) {
  return useQuery({
    queryKey: ['batch-summaries', { state, limit }],
    queryFn: () => api.listBatchSummaries({ state, limit }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
